import { IconInfoCircle } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  INBOX_HEADLINE_MAX,
  INBOX_PREVIEW_MAX,
  InboxVariant,
  isInboxCtaUrlValid,
} from './inbox-variant';
import { InboxMessagePreview } from './InboxMessagePreview';
import { InboxMediaSelector } from './InboxMediaSelector';
import { InboxMediaUpload } from './InboxMediaUpload';

/**
 * In-app inbox content for a multi-channel campaign.
 *
 * The counters bound the TEMPLATE, which is what the operator can act on. The
 * rendered value can still exceed the column for one recipient with a very
 * long first name; Nova fails that recipient and commits the rest, so this is
 * guidance rather than a hard gate on the send.
 */
export function InboxVariantEditor({
  value,
  onChange,
  disabled,
}: {
  value: InboxVariant;
  onChange: (variant: InboxVariant) => void;
  disabled?: boolean;
}) {
  const set = <K extends keyof InboxVariant>(
    key: K,
    fieldValue: InboxVariant[K],
  ) => onChange({ ...value, [key]: fieldValue });

  const headlineOver = value.headline.length > INBOX_HEADLINE_MAX;
  const previewOver = value.preview.length > INBOX_PREVIEW_MAX;
  const ctaUrlInvalid = !isInboxCtaUrlValid(value.ctaUrl);
  // Scheduled campaigns created before CWORK-634 do not carry this property.
  // Keep them editable instead of requiring a data migration just to open the
  // composer.
  const companionPush = value.companionPush ?? {
    enabled: false,
    title: '',
    body: '',
    imageUrl: '',
  };

  return (
    <div className="space-y-4 rounded-lg border border-border p-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">In-App Inbox Message</h4>
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <IconInfoCircle size={14} />
          {'{{first_name}}, {{surname}} and {{brand}} are supported'}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="inbox-headline">Headline</Label>
        <Input
          id="inbox-headline"
          value={value.headline}
          onChange={(event) => set('headline', event.target.value)}
          placeholder="Winter promo is here"
          disabled={disabled}
          className={cn(headlineOver && 'border-destructive')}
        />
        <p className="text-xs text-muted-foreground">
          {value.headline.length}/{INBOX_HEADLINE_MAX} characters
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="inbox-preview">Preview</Label>
        <Textarea
          id="inbox-preview"
          rows={2}
          value={value.preview}
          onChange={(event) => set('preview', event.target.value)}
          placeholder="Short line shown in the notification list"
          disabled={disabled}
          className={cn(previewOver && 'border-destructive')}
        />
        <p className="text-xs text-muted-foreground">
          {value.preview.length}/{INBOX_PREVIEW_MAX} characters
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="inbox-full-message">Full Message</Label>
        <Textarea
          id="inbox-full-message"
          rows={5}
          value={value.fullMessage}
          onChange={(event) => set('fullMessage', event.target.value)}
          placeholder="The full message body shown when the customer opens the notification"
          disabled={disabled}
        />
      </div>

      <InboxMediaSelector
        value={value}
        onChange={onChange}
        disabled={disabled}
      />

      <div className="space-y-1.5">
        <Label htmlFor="inbox-cta-url">Link URL (optional)</Label>
        <Input
          id="inbox-cta-url"
          value={value.ctaUrl}
          onChange={(event) => set('ctaUrl', event.target.value)}
          placeholder="https://example.com/promo or /rewards"
          disabled={disabled}
          className={cn(ctaUrlInvalid && 'border-destructive')}
        />
        <p
          className={cn(
            'text-xs',
            ctaUrlInvalid ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {ctaUrlInvalid
            ? 'Must be an https:// URL or an in-app path starting with a single /.'
            : 'External https URL or internal app path. Where the message taps through to; leave blank for no link.'}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label>Category</Label>
        <p className="text-sm font-medium">{value.category}</p>
        <p className="text-xs text-muted-foreground">
          Automatically set to Message for text-only content and Promotion when
          media is selected.
        </p>
      </div>

      <InboxMessagePreview value={value} />

      <div className="space-y-3 border-t border-border pt-4">
        <div className="flex items-start gap-2">
          <Checkbox
            id="inbox-companion-push"
            checked={companionPush.enabled}
            onCheckedChange={(checked) =>
              set('companionPush', {
                ...companionPush,
                enabled: checked === true,
              })
            }
            disabled={disabled}
          />
          <div>
            <Label htmlFor="inbox-companion-push">
              Send companion push notification
            </Label>
            <p className="text-xs text-muted-foreground">
              Optional. The Inbox audience is unchanged; users without
              notifications enabled still receive the Inbox message.
            </p>
          </div>
        </div>
        {companionPush.enabled && (
          <div className="space-y-3 rounded-md border border-border p-3">
            <div className="space-y-1.5">
              <Label htmlFor="companion-push-title">Push title</Label>
              <Input
                id="companion-push-title"
                value={companionPush.title}
                onChange={(event) =>
                  set('companionPush', {
                    ...companionPush,
                    title: event.target.value,
                  })
                }
                placeholder="You have a new message"
                disabled={disabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="companion-push-body">Push message</Label>
              <Textarea
                id="companion-push-body"
                rows={3}
                value={companionPush.body}
                onChange={(event) =>
                  set('companionPush', {
                    ...companionPush,
                    body: event.target.value,
                  })
                }
                placeholder="Open your inbox to view it"
                disabled={disabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Push image (optional)</Label>
              <InboxMediaUpload
                imageOnly
                imageUrl={companionPush.imageUrl}
                videoUrl=""
                onChange={(patch) =>
                  set('companionPush', {
                    ...companionPush,
                    imageUrl: patch.imageUrl ?? '',
                  })
                }
                disabled={disabled}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Tapping the notification opens the app's Notifications screen.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
